import { useMemo, useState } from "react";

/** 상단 탭 */
function TabBar({ tab, setTab }) {
    return (
        <div className="tabbar" role="tablist" aria-label="환경설정 탭">
            <button
                type="button"
                role="tab"
                aria-selected={tab === "users"}
                className={`tabbar__btn ${tab === "users" ? "is-active" : ""}`}
                onClick={() => setTab("users")}
            >
                사용자 관리
            </button>
            <button
                type="button"
                role="tab"
                aria-selected={tab === "services"}
                className={`tabbar__btn ${tab === "services" ? "is-active" : ""}`}
                onClick={() => setTab("services")}
            >
                서비스 관리
            </button>
        </div>
    );
}

/** 상태 배지 */
function StatusBadge({ status }) {
    const map = {
        요청: "pending",
        승인: "approved",
        거절: "rejected",
        보류: "review",
        보기: "review",
    };
    const cls = map[status] || "review";
    return <span className={`tag tag--${cls}`}>{status}</span>;
}

/** 사용자 관리 탭 */
function UsersTab() {
    // 데모 데이터
    const [rows, setRows] = useState([
        { id: 1, name: "사용자 이름", joined: "2025 / 07 / 05", status: "요청" },
        { id: 2, name: "사용자 이름", joined: "2025 / 07 / 05", status: "승인" },
        { id: 3, name: "사용자 이름", joined: "2025 / 07 / 05", status: "거절" },
        { id: 4, name: "사용자 이름", joined: "2025 / 07 / 05", status: "보기" },
    ]);
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [pickerOpen, setPickerOpen] = useState(false);
    const [nextStatus, setNextStatus] = useState("승인");

    const filtered = useMemo(() => {
        if (!query.trim()) return rows;
        const q = query.trim();
        return rows.filter(
            (r) => r.name.includes(q) || r.joined.includes(q) || r.status.includes(q)
        );
    }, [rows, query]);

    const allChecked = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
    const someChecked = filtered.some((r) => selected.has(r.id));

    const toggleAll = () => {
        const next = new Set(selected);
        if (allChecked) {
            filtered.forEach((r) => next.delete(r.id));
        } else {
            filtered.forEach((r) => next.add(r.id));
        }
        setSelected(next);
    };

    const toggleRow = (id) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };

    const applyStatusChange = () => {
        if (selected.size === 0) return;
        setRows((prev) =>
            prev.map((r) => (selected.has(r.id) ? { ...r, status: nextStatus } : r))
        );
        setPickerOpen(false);
    };

    const removeUsers = () => {
        if (selected.size === 0) return;
        if (!window.confirm("선택한 사용자를 삭제하시겠습니까?")) return;
        setRows((prev) => prev.filter((r) => !selected.has(r.id)));
        setSelected(new Set());
    };

    return (
        <section className="subsec">
            <h2 className="subsec__title">사용자 관리</h2>

            {/* 검색 상자 (우측 상단) */}
            <div className="searchbox">
                <input
                    type="text"
                    placeholder="검색"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label="사용자 검색"
                />
                <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    className="searchbox__icon"
                    aria-hidden
                >
                    <circle cx="11" cy="11" r="7" stroke="currentColor" fill="none" strokeWidth="2" />
                    <line x1="16.5" y1="16.5" x2="22" y2="22" stroke="currentColor" strokeWidth="2" />
                </svg>
            </div>

            <div className="table table--users">
                <div className="table__head">
                    <div>
                        <label className={`chk ${someChecked ? "is-some" : ""}`}>
                            <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={toggleAll}
                                aria-checked={allChecked ? "true" : someChecked ? "mixed" : "false"}
                            />
                            <span />
                        </label>
                    </div>
                    <div>사용자 이름</div>
                    <div>가입 날짜</div>
                    <div>상태</div>
                </div>

                <div className="table__body table__body--scroll">
                    {filtered.map((r) => {
                        const isSel = selected.has(r.id);
                        return (
                            <div
                                key={r.id}
                                className={`table__row ${isSel ? "table__row--selected" : ""}`}
                                onClick={() => toggleRow(r.id)}
                                role="row"
                            >
                                <div>
                                    <label className="chk" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={isSel}
                                            onChange={() => toggleRow(r.id)}
                                            aria-label={`${r.name} 선택`}
                                        />
                                        <span />
                                    </label>
                                </div>
                                <div className="cell-link">{r.name}</div>
                                <div>{r.joined}</div>
                                <div>
                                    <StatusBadge status={r.status} />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* 하단 액션 바 */}
                <div className="actionbar">
                    <div className="actionbar__left">
                        <div className="btn-group">
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => setPickerOpen((v) => !v)}
                                disabled={selected.size === 0}
                                title="선택 사용자 상태 변경"
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="currentColor" />
                                </svg>
                                사용자 상태변경
                            </button>

                            {pickerOpen && (
                                <>
                                    <select
                                        className="actionbar__select"
                                        value={nextStatus}
                                        onChange={(e) => setNextStatus(e.target.value)}
                                    >
                                        <option>요청</option>
                                        <option>승인</option>
                                        <option>거절</option>
                                        <option>보류</option>
                                    </select>
                                    <button type="button" className="btn" onClick={applyStatusChange}>
                                        적용
                                    </button>
                                </>
                            )}
                        </div>

                        <button
                            type="button"
                            className="btn"
                            onClick={removeUsers}
                            disabled={selected.size === 0}
                            title="선택 사용자 삭제"
                        >
                            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                                <path d="M6 7h12l-1 14H7L6 7zm2-3h8l1 3H7l1-3z" fill="currentColor" />
                            </svg>
                            사용자 삭제
                        </button>
                    </div>

                    <div className="actionbar__right">
                        <button type="button" className="btn-primary" onClick={() => { }}>
                            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                                <circle cx="11" cy="11" r="7" stroke="currentColor" fill="none" strokeWidth="2" />
                                <line x1="16.5" y1="16.5" x2="22" y2="22" stroke="currentColor" strokeWidth="2" />
                            </svg>
                            사용자 검색
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}

/** 서비스 관리 탭 */
function ServicesTab() {
    const [items, setItems] = useState([
        {
            id: 1,
            name: "중앙소방학교",
            cover:
                "https://images.unsplash.com/photo-1512914890255-dc08aea9ce87?q=80&w=1200&auto=format&fit=crop",
        },
        {
            id: 2,
            name: "현대아울렛",
            cover:
                "https://images.unsplash.com/photo-1501183638710-841dd1904471?q=80&w=1200&auto=format&fit=crop",
        },
    ]);

    const addService = () => {
        // TODO: 실제 신규 서비스 생성 로직
        alert("신규 서비스 생성");
    };

    const openService = (id) => {
        // TODO: 상세/편집 페이지로 이동
        alert(`서비스 열기: ${id}`);
    };

    return (
        <section className="subsec">
            <h2 className="subsec__title">서비스 관리</h2>

            <div className="svc-grid">
                {items.map((s) => (
                    <button
                        key={s.id}
                        type="button"
                        className="svc-card"
                        onClick={() => openService(s.id)}
                        style={{ backgroundImage: `url(${s.cover})` }}
                        aria-label={`${s.name} 열기`}
                    >
                        <div className="svc-card__title">{s.name}</div>
                    </button>
                ))}

                <button type="button" className="svc-card svc-card--new" onClick={addService}>
                    <div className="svc-card__plus" aria-hidden>+</div>
                    신규 서비스
                </button>
            </div>
        </section>
    );
}

export default function Settings() {
    const [tab, setTab] = useState("users"); // 'users' | 'services'
    return (
        <div className="settings">
            <TabBar tab={tab} setTab={setTab} />
            {tab === "users" ? <UsersTab /> : <ServicesTab />}
        </div>
    );
}
